import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppSelector } from '@/hooks';
import { useUI } from '@/hooks/ui/useUI';
import { useTableSearch } from '@/hooks/ui/table/useTableSearch';
import { formatDateToYYYYMMDD } from '@/utils';
import { shiftService } from '@/api/domains/shift';
import { SHIFT_REQUEST_STATUS } from '@/api/domains/shift/shift.types';
import type { ShiftRequest, ShiftRequestStatus } from '@/api/domains/shift/shift.types';
import type { TableColumn, TableAction } from '@/components/ui/tables/Table';
import { colors } from '@/assets/styles/colors';


export interface ShiftRequestFilters {
  status?: ShiftRequestStatus | 'all';
  dateFrom?: string;
  dateTo?: string;
}

export const useShiftChangeRequests = () => {
  const { t: translate } = useTranslation();
  const { openSidePanel, openModal, closeModal, showNotification, showApiError } = useUI();
  
  // Get employeeId from auth state
  const employeeId = useAppSelector((state: any) => state.auth.user?.idEmpleado);

  const getDefaultDateRange = (): { from: string; to: string } => {
    const currentDate = new Date();
    const fromDate = new Date();
    fromDate.setDate(currentDate.getDate() - 30); // Last 30 days
    return {
      from: formatDateToYYYYMMDD(fromDate),
      to: formatDateToYYYYMMDD(currentDate),
    };
  };

  const [filters, setFilters] = useState<ShiftRequestFilters>(() => ({
    status: 'all',
    ...getDefaultDateRange(),
  }));

  // Memoize default values for DateRangePicker
  const defaultFrom = useMemo(() => {
    const currentDate = new Date();
    const fromDate = new Date();
    fromDate.setDate(currentDate.getDate() - 30);
    return formatDateToYYYYMMDD(fromDate);
  }, []);

  const defaultTo = useMemo(() => formatDateToYYYYMMDD(new Date()), []);

  type FiltersUpdater = ShiftRequestFilters | ((prev: ShiftRequestFilters) => ShiftRequestFilters);
  
  const setFiltersSafe = useCallback((updater: FiltersUpdater) => {
    setFilters((previousFilters) =>
      typeof updater === 'function'
        ? (updater as (currentFilters: ShiftRequestFilters) => ShiftRequestFilters)(previousFilters)
        : updater,
    );
  }, []);

  // Server-side list state
  const [rows, setRows] = useState<ShiftRequest[]>([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const { debouncedSearchText, setSearchText } = useTableSearch({
    onDebouncedChange: () => setPage(1),
  });
  const [sort, setSort] = useState<{ orderBy?: string; direction?: 'ASC' | 'DESC' }>({});
  const [isLoading, setIsLoading] = useState(false);

  // Map UI columns to backend fields
  // Backend accepts: id | employeeName | requestDate | effectiveDate | status
  const orderByMap: Record<string, string> = useMemo(
    () => ({
      id: 'id',
      requestDate: 'requestDate',
      dateFrom: 'effectiveDate',  // API uses effectiveDate for ordering
      dateTo: 'effectiveDate',     // API uses effectiveDate for ordering
      status: 'status',
    }),
    [],
  );

  const fetchList = useCallback(async () => {
    if (!employeeId) {
      showNotification('error', translate('attendance:shiftChange.messages.loadError'), 4000);
      return;
    }

    setIsLoading(true);

    const response = await shiftService.getShiftRequests({
      employeeId,
      status: filters.status === 'all' ? undefined : filters.status,
      dateFrom: filters.dateFrom,
      dateTo: filters.dateTo,
      page,
      pageSize,
      searchText: debouncedSearchText || undefined,
      orderBy: sort.orderBy,
      orderDirection: sort.direction,
    });

    if (!response.success || !response.data) {
      showApiError(response, translate('attendance:shiftChange.messages.loadError'));
      setRows([]);
      setTotalRecords(0);
      setIsLoading(false);
      return;
    }

    setRows(response.data.data);
    setTotalRecords(response.data.totalRecords);
    setIsLoading(false);
  }, [
    employeeId,
    filters.status,
    filters.dateFrom,
    filters.dateTo,
    page,
    pageSize,
    debouncedSearchText,
    sort.orderBy,
    sort.direction,
    showApiError,
    showNotification,
    translate,
  ]);

  useEffect(() => {
    void fetchList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    employeeId,
    filters.status,
    filters.dateFrom,
    filters.dateTo,
    page,
    pageSize,
    debouncedSearchText,
    sort.orderBy,
    sort.direction,
  ]);

  const onSearch = useCallback((text: string) => {
    setSearchText(text);
  }, [setSearchText]);

  const onSortChange = useCallback(
    (columnKey: string, direction: 'asc' | 'desc') => {
      setSort({
        orderBy: orderByMap[columnKey] ?? undefined,
        direction: direction.toUpperCase() as 'ASC' | 'DESC',
      });
      setPage(1);
    },
    [orderByMap],
  );

  const openNewRequestPanel = useCallback(() => {
    openSidePanel({
      title: translate('attendance:shiftChange.form.newRequest'),
      component: 'ShiftRequestForm',
      props: {
        onSuccess: () => {
          void fetchList();
        },
      },
    });
  }, [openSidePanel, fetchList, translate]);

  const handleCancel = useCallback(
    (requestId: number) => {
      if (!employeeId) return;

      openModal(
        'ConfirmModal',
        {
          variant: 'alert',
          icon: 'alert',
          title: translate('attendance:shiftChange.messages.cancelTitle'),
          bodyText: translate('attendance:shiftChange.messages.cancelConfirm', { id: requestId }),
          confirmText: translate('common:actions.confirm'),
          cancelText: translate('common:actions.cancel'),
          onConfirm: async () => {
            const response = await shiftService.deleteShiftRequest(employeeId, requestId);

            if (!response.success) {
              showApiError(response, translate('attendance:shiftChange.messages.cancelError'));
              return;
            }

            showNotification('success', translate('attendance:shiftChange.messages.cancelSuccess'), 4000);
            closeModal('ConfirmModal');
            void fetchList();
          },
          onCancel: () => {
            closeModal('ConfirmModal');
          },
        },
      );
    },
    [employeeId, fetchList, showApiError, showNotification, translate, openModal, closeModal],
  );

  // Table columns configuration
  const columns: TableColumn<ShiftRequest>[] = useMemo(
    () => [
      {
        key: 'id',
        header: translate('attendance:shiftChange.table.id'),
        sortable: true,
      },
      {
        key: 'requestDate',
        header: translate('attendance:shiftChange.table.requestDate'),
        sortable: true,
      },
      {
        key: 'comments',
        header: translate('attendance:shiftChange.table.comments'),
        sortable: false,
        render: (row: ShiftRequest) => (
          <div className="max-w-xs truncate" title={row.comments}>
            {row.comments || '-'}
          </div>
        ),
      },
      {
        key: 'dateFrom',
        header: translate('attendance:shiftChange.table.dateFrom'),
        sortable: true,
      },
      {
        key: 'dateTo',
        header: translate('attendance:shiftChange.table.dateTo'),
        sortable: true,
      },
      {
        key: 'status',
        header: translate('attendance:shiftChange.table.status'),
        sortable: true,
      },
    ],
    [translate],
  );

  // Table actions configuration
  const actions: TableAction<ShiftRequest>[] = useMemo(
    () => [
      {
        id: 'cancel',
        label: translate('attendance:shiftChange.actions.cancel'),
        onClick: (row: ShiftRequest) => handleCancel(row.id),
        icon: 'close',
        color: colors.feedback.error400,
        hidden: (row: ShiftRequest) => row.statusCode !== SHIFT_REQUEST_STATUS.PENDING,
      },
    ],
    [handleCancel, translate],
  );

  return {
    // State
    filters,
    setFilters: setFiltersSafe,
    defaultFrom,
    defaultTo,
    data: rows,
    totalRecords,
    isLoading,
    page,
    pageSize,
    
    // Actions
    setPage,
    setPageSize,
    onSearch,
    onSortChange,
    openNewRequestPanel,
    
    // Table config
    columns,
    actions,
  };
};
